import type { Api, Filter } from "../../../data";
import {
  PAGE_SIZE, checkFile, chatStoragePath, displayFileName, messageBody, mimeOf, unreadSummary,
  type AttachmentRow, type ContractLite, type DealLite, type InvoiceFacts, type InvoiceLite, type LinkKind, type LinkObjects, type MemberLite, type Mentionable,
  type MessageRow, type NoteRow, type NotificationItem, type NotificationRow, type Portal, type ReactionRow, type Side, type TalentLite, type ThreadRow, type UnreadCount,
} from "./model";
import { t } from "../../../lib/sprache";

/**
 * Lader und Schreibwege für Nachrichten und Benachrichtigungen. Jede Funktion bekommt den Anschluss, damit die Tests
 * genau diese Wege gegen die Demo-Datenbank laufen lassen. Jede Abfrage filtert nach der Agentur des Arbeitsbereichs
 * (und im Creator-Portal nach dem eigenen Talent-Datensatz); RLS begrenzt zusätzlich, was ankommt.
 * Schreiben nur über die Funktionen der Datenbank (chat_send, chat_mark_read, chat_react, notification_*,
 * invoice_report_payment) sowie Datei plus Anhangzeile nach der Pfadregel aus 0013.
 */

const MESSAGE_COLUMNS = "id, org_id, talent_id, sender_user_id, sender_side, body, link_kind, link_id, created_at, read_at, retracted_at, broadcast_id, reply_to";
const NOTE_COLUMNS = "id, org_id, talent_id, author_user_id, body, mentions, created_at";
const NOTIFICATION_COLUMNS = "id, org_id, type, is_task, color, audience, talent_id, params, link_kind, link_id, created_at, read_at, unread, sort_order, assigned_to, assigned_name";

// ── Nachrichten ─────────────────────────────────────────────────────────────

export interface AgencyChatBase {
  threads: ThreadRow[];
  talents: TalentLite[];
}

/** Gesprächsliste der Agentur: je sichtbarem Creator ein Faden (Kim Feld sieht nur ihre zugewiesenen Creator). */
export async function loadAgencyThreads(api: Api, orgId: string): Promise<AgencyChatBase> {
  const [threads, talents] = await Promise.all([
    api.select<ThreadRow>("my_chat_threads", { filters: [["org_id", "eq", orgId], ["my_side", "eq", "agency"]] }),
    api.select<TalentLite>("talents", { columns: "id, first_name, last_name, display_name, status", filters: [["org_id", "eq", orgId]], order: [["first_name", "asc"]] }),
  ]);
  return { threads, talents };
}

/** Der eine Faden des Creators mit seiner Agentur (nie die Fäden anderer Agenturen desselben Kontos). */
export async function loadCreatorThread(api: Api, orgId: string, talentId: string): Promise<ThreadRow | null> {
  const rows = await api.select<ThreadRow>("my_chat_threads", {
    filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId], ["my_side", "eq", "talent"]],
  });
  return rows[0] ?? null;
}

export interface ThreadData {
  messages: MessageRow[];
  reactions: ReactionRow[];
  attachments: AttachmentRow[];
  /** Es gibt ältere Nachrichten als die geladenen */
  hasMore: boolean;
  /** Interne Notizen im Zeitraum der geladenen Nachrichten (nur Team, 0393; für den Creator immer leer) */
  notes: NoteRow[];
  /** Zitierte Nachrichten, die älter sind als die geladenen (für die Zeile über der Antwort) */
  quoted: MessageRow[];
}

/**
 * Die neuesten `limit` Nachrichten eines Fadens (chronologisch), mit Reaktionen, Dateien und zitierten Nachrichten.
 * Mit `notes` (Agentur) dazu die internen Notizen aus demselben Zeitraum; die Datenbank liefert sie nur dem Team.
 */
export async function loadThread(api: Api, orgId: string, talentId: string, limit = PAGE_SIZE, opts: { notes?: boolean } = {}): Promise<ThreadData> {
  const rows = await api.select<MessageRow>("messages", {
    columns: MESSAGE_COLUMNS,
    filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId]],
    order: [["created_at", "desc"], ["id", "desc"]],
    limit: limit + 1,
  });
  const hasMore = rows.length > limit;
  const messages = rows.slice(0, limit).reverse();
  const ids = messages.map((m) => m.id);
  const since = hasMore ? messages[0]?.created_at ?? null : null;
  const notesQ = opts.notes
    ? api.select<NoteRow>("message_notes", {
        columns: NOTE_COLUMNS,
        filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId], ...(since ? [["created_at", "gte", since] as const] : [])],
        order: [["created_at", "asc"]],
        limit: 1000,
      }).catch(() => [] as NoteRow[])
    : Promise.resolve([] as NoteRow[]);
  if (!ids.length) return { messages, reactions: [], attachments: [], hasMore, notes: await notesQ, quoted: [] };
  const missing = [...new Set(messages.map((m) => m.reply_to).filter((x): x is string => !!x && !ids.includes(x)))];
  const [reactions, attachments, notes, quoted] = await Promise.all([
    api.select<ReactionRow>("message_reactions", { columns: "message_id, user_id, emoji, created_at", filters: [["message_id", "in", ids]] }),
    api.select<AttachmentRow>("attachments", {
      columns: "id, owner_id, storage_path, file_name, mime_type, size_bytes, created_at, uploaded_by",
      filters: [["owner_kind", "eq", "chat"], ["owner_id", "in", ids], ["deleted_at", "is", null]],
      order: [["created_at", "asc"]],
    }),
    notesQ,
    missing.length
      ? api.select<MessageRow>("messages", { columns: MESSAGE_COLUMNS, filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId], ["id", "in", missing]] })
      : Promise.resolve([] as MessageRow[]),
  ]);
  return { messages, reactions, attachments, hasMore, notes, quoted };
}

/** Namen des Teams für die Absenderzeile im Agentur-Portal (Creator können das Team nicht lesen, 0004). */
export async function loadMembers(api: Api, orgId: string): Promise<MemberLite[]> {
  return api.select<MemberLite>("memberships", { columns: "user_id, display_name", filters: [["org_id", "eq", orgId]] });
}

async function brandsOf(api: Api, orgId: string, ids: readonly (string | null)[], table: "brands" | "talent_brands" = "brands"): Promise<Record<string, string>> {
  const unique = [...new Set(ids.filter((x): x is string => !!x))];
  if (!unique.length) return {};
  const rows = await api.select<{ id: string; name: string }>(table, { columns: "id, name", filters: [["org_id", "eq", orgId], ["id", "in", unique]] });
  return Object.fromEntries(rows.map((b) => [b.id, b.name]));
}

/** Kooperationen, Rechnungen und Verträge eines Creators aus Sicht des Teams (für Karten und die Auswahl „Verknüpfen“). */
export async function loadAgencyLinkObjects(api: Api, orgId: string, talentId: string): Promise<LinkObjects> {
  const byTalent: Filter[] = [["org_id", "eq", orgId], ["talent_id", "eq", talentId]];
  const [deals, invoices, contracts] = await Promise.all([
    api.select<DealLite>("deals", { columns: "id, title, status, brand_id, visible_to_talent, archived_at, created_at, start_month", filters: byTalent, order: [["created_at", "desc"]] }),
    api.select<InvoiceLite>("invoices", { columns: "id, number, status, kind, gross_cents, currency, brand_id, deal_id, created_at, effective_due_date", filters: byTalent, order: [["created_at", "desc"]] }),
    api.select<ContractLite>("contracts", { columns: "id, title, status, created_at, contract_number, contract_number_label, brand_id", filters: byTalent, order: [["created_at", "desc"]] }),
  ]);
  const brands = await brandsOf(api, orgId, [...deals.map((d) => d.brand_id), ...invoices.map((i) => i.brand_id), ...contracts.map((c) => c.brand_id ?? null)]);
  return { deals, invoices, contracts, brands };
}

/** Dasselbe aus Sicht des Creators: nur sichtbare Kooperationen, eigene Rechnungen, Verträge über die Sicht talent_contracts (0031). */
export async function loadCreatorLinkObjects(api: Api, orgId: string, talentId: string): Promise<LinkObjects> {
  const byTalent: Filter[] = [["org_id", "eq", orgId], ["talent_id", "eq", talentId]];
  const [deals, invoices, contracts] = await Promise.all([
    api.select<DealLite>("talent_deals", { columns: "id, title, status, brand_id, created_at, start_month", filters: byTalent, order: [["created_at", "desc"]] }),
    api.select<InvoiceLite>("invoices", {
      columns: "id, number, status, kind, gross_cents, currency, brand_id, deal_id, created_at, effective_due_date",
      filters: [...byTalent, ["kind", "in", ["talent_to_agency", "talent_to_brand", "agency_to_talent"]]],
      order: [["created_at", "desc"]],
    }),
    api.select<ContractLite>("talent_contracts", { columns: "id, title, status, created_at, contract_number, contract_number_label, brand_id", filters: byTalent, order: [["created_at", "desc"]] }),
  ]);
  const brands = await brandsOf(api, orgId, [...deals.map((d) => d.brand_id), ...invoices.map((i) => i.brand_id), ...contracts.map((c) => c.brand_id ?? null)], "talent_brands");
  return { deals, invoices, contracts, brands };
}

export interface SendInput {
  orgId: string;
  talentId: string;
  /** Seite, für die gesendet wird (wichtig bei Doppelrolle: Team-Mitglied und zugleich Creator, 0041) */
  side?: Side;
  body: string;
  link: { kind: LinkKind; id: string } | null;
  files: readonly File[];
  /** Antwort mit Zitat (0393) */
  replyTo?: string | null;
}
export interface SendResult {
  messageId: string;
  /** Dateien, die nicht hochgeladen wurden (die Nachricht ist trotzdem gesendet) */
  failed: { file: File; error: string }[];
}

/**
 * Nachricht senden (chat_send setzt Absender und Seite) und Dateien anhängen. Vorher prüft die Seite die Dateien;
 * die Datenbank prüft Art, Größe und ob die Nachricht der sendenden Person gehört und höchstens eine Stunde alt ist.
 */
export async function sendMessage(api: Api, input: SendInput): Promise<SendResult> {
  const problems = input.files.map((f) => checkFile(f)).filter((x): x is string => !!x);
  if (problems.length) throw new Error(problems.join(" "));
  const body = messageBody(input.body, input.files.map((f) => f.name));
  const messageId = await api.rpc<string>("chat_send", {
    p_talent: input.talentId,
    p_body: body,
    p_link_kind: input.link?.kind,
    p_link_id: input.link?.id,
    ...(input.side ? { p_side: input.side } : {}),
    ...(input.replyTo ? { p_reply_to: input.replyTo } : {}),
  });
  if (!messageId) throw new Error(t("Die Nachricht wurde nicht gespeichert.", "The message was not saved."));
  const failed = await attachFiles(api, input.orgId, messageId, input.files);
  return { messageId, failed };
}

/** Dateien an eine eigene, höchstens eine Stunde alte Nachricht hängen: erst die Datei, dann die Zeile in attachments. */
export async function attachFiles(api: Api, orgId: string, messageId: string, files: readonly File[]): Promise<{ file: File; error: string }[]> {
  const failed: { file: File; error: string }[] = [];
  const stamp = Date.now();
  let i = 0;
  for (const file of files) {
    i += 1;
    const problem = checkFile(file);
    if (problem) {
      failed.push({ file, error: problem });
      continue;
    }
    const mime = mimeOf(file)!;
    const blob = file.type === mime ? file : new Blob([file], { type: mime });
    const path = chatStoragePath(orgId, messageId, file.name, stamp, i);
    try {
      await api.upload("documents", path, blob);
      await api.insert("attachments", {
        org_id: orgId, owner_kind: "chat", owner_id: messageId, storage_path: path,
        file_name: displayFileName(file.name), mime_type: mime, size_bytes: file.size,
      }, { columns: "id" });
    } catch (e) {
      failed.push({ file, error: e instanceof Error ? e.message : String(e) });
    }
  }
  return failed;
}

/**
 * Doppeltes Senden vermeiden (08_komponenten: „Retry deduplizieren“): Kam die Nachricht beim ersten Versuch doch an
 * (Verbindung brach nach dem Speichern ab), liefert diese Abfrage ihre Kennung, und die Seite sendet nicht erneut.
 */
export async function findSentMessage(api: Api, input: { orgId: string; talentId: string; userId: string; body: string; since: string }): Promise<string | null> {
  const rows = await api.select<MessageRow>("messages", {
    columns: "id, body, sender_user_id, created_at",
    filters: [["org_id", "eq", input.orgId], ["talent_id", "eq", input.talentId], ["sender_user_id", "eq", input.userId], ["created_at", "gte", input.since]],
    order: [["created_at", "desc"]],
    limit: 50,
  });
  return rows.find((r) => r.body === input.body)?.id ?? null;
}

/** Welche Dateien hängen noch nicht an der Nachricht (nach einem abgebrochenen Versand)? Vergleich über den Dateinamen. */
export async function missingFiles(api: Api, messageId: string, files: readonly File[]): Promise<File[]> {
  if (!files.length) return [];
  const rows = await api.select<{ file_name: string }>("attachments", { columns: "file_name", filters: [["owner_kind", "eq", "chat"], ["owner_id", "eq", messageId]] });
  const have = new Set(rows.map((r) => r.file_name));
  return files.filter((f) => !have.has(displayFileName(f.name)));
}

/** Interne Notiz im Faden (nur Team, 0393); Erwähnte bekommen einen Hinweis in der Glocke. */
export async function addNote(api: Api, talentId: string, body: string, mentions: readonly string[]): Promise<string> {
  return api.rpc<string>("chat_note_add", { p_talent: talentId, p_body: body, p_mentions: [...mentions] });
}

/** Wen das Team in einer Notiz zu diesem Creator erwähnen kann (0393). */
export async function loadMentionable(api: Api, talentId: string): Promise<Mentionable[]> {
  return (await api.read<Mentionable[] | null>("chat_note_mentionable", { p_talent: talentId }).catch(() => null)) ?? [];
}

/** Eingehende Nachrichten eines Fadens als gelesen markieren (gilt für die ganze Seite, A-23). */
export async function markThreadRead(api: Api, talentId: string, side?: Side): Promise<number> {
  return api.rpc<number>("chat_mark_read", { p_talent: talentId, ...(side ? { p_side: side } : {}) });
}

/** Eigene Nachricht zurückziehen (0041): Text, Verknüpfung und Dateien verschwinden; es bleibt „zurückgezogen“. */
export async function retractMessage(api: Api, messageId: string): Promise<void> {
  await api.rpc("chat_retract", { p_message: messageId });
}

/** Reaktion setzen oder zurücknehmen; true = gesetzt. */
export async function toggleReaction(api: Api, messageId: string, emoji: string): Promise<boolean> {
  return api.rpc<boolean>("chat_react", { p_message: messageId, p_emoji: emoji });
}

/** Datei aus dem privaten Bucket (lesen darf, wer die Nachricht lesen darf). */
export async function fetchAttachment(api: Api, path: string): Promise<Blob> {
  return api.download("documents", path);
}

// ── Benachrichtigungen ──────────────────────────────────────────────────────

export interface NotificationScope {
  orgId: string;
  audience: "staff" | "talent";
  /** nur im Creator-Portal: der eigene Talent-Datensatz */
  talentId?: string | null;
}
function scopeFilters(s: NotificationScope): Filter[] {
  const f: Filter[] = [["org_id", "eq", s.orgId], ["audience", "eq", s.audience]];
  if (s.talentId) f.push(["talent_id", "eq", s.talentId]);
  return f;
}

/** Glocke: offene Aufgaben und nicht ausgeblendete Hinweise der angemeldeten Person (Sicht my_notifications). */
export async function loadOpenNotifications(api: Api, s: NotificationScope): Promise<NotificationItem[]> {
  const rows = await api.select<NotificationRow>("my_notifications", { columns: NOTIFICATION_COLUMNS, filters: scopeFilters(s), order: [["created_at", "desc"]], limit: 300 });
  return rows.map((r) => ({ ...r, params: r.params ?? {}, state: "open" as const, closed_at: null }));
}

interface NotificationBase {
  id: string;
  org_id: string;
  type: string;
  audience: "staff" | "talent";
  talent_id: string | null;
  params: Record<string, unknown> | null;
  link_kind: string | null;
  link_id: string | null;
  created_at: string;
  resolved_at: string | null;
  resolve_reason?: string | null;
}
interface TypeRow {
  key: string;
  is_task: boolean;
  color: string;
  sort_order: number;
}

/**
 * Erledigtes: Aufgaben, die die Datenbank abgeschlossen hat (resolved_at), und Hinweise, die diese Person ausgeblendet
 * hat. Beides nur lesend; die Datenbank löscht Erledigtes nach zwölf Monaten (AU-035).
 */
export async function loadDoneNotifications(api: Api, s: NotificationScope, userId: string, limit = 150): Promise<NotificationItem[]> {
  const base = scopeFilters(s);
  const cols = "id, org_id, type, audience, talent_id, params, link_kind, link_id, created_at, resolved_at, resolve_reason";
  const [resolved, states, types] = await Promise.all([
    api.select<NotificationBase>("notifications", { columns: cols, filters: [...base, ["resolved_at", "not_is", null]], order: [["resolved_at", "desc"]], limit }),
    api.select<{ notification_id: string; read_at: string | null; dismissed_at: string | null }>("notification_states", {
      columns: "notification_id, read_at, dismissed_at", filters: [["user_id", "eq", userId], ["dismissed_at", "not_is", null]],
    }),
    api.select<TypeRow>("notification_types", { columns: "key, is_task, color, sort_order" }),
  ]);
  const dismissedAt = new Map(states.map((x) => [x.notification_id, x.dismissed_at]));
  const dismissedIds = [...dismissedAt.keys()];
  const dismissed = dismissedIds.length
    ? await api.select<NotificationBase>("notifications", { columns: cols, filters: [...base, ["id", "in", dismissedIds], ["resolved_at", "is", null]], order: [["created_at", "desc"]], limit })
    : [];
  const type = new Map(types.map((t) => [t.key, t]));
  const item = (n: NotificationBase, state: "resolved" | "dismissed", closed: string | null): NotificationItem => ({
    id: n.id, org_id: n.org_id, type: n.type, audience: n.audience, talent_id: n.talent_id, params: n.params ?? {}, link_kind: n.link_kind, link_id: n.link_id,
    created_at: n.created_at, read_at: null, unread: false, is_task: type.get(n.type)?.is_task ?? state === "resolved",
    color: type.get(n.type)?.color ?? "grey", sort_order: type.get(n.type)?.sort_order ?? 100, state, closed_at: closed,
    resolve_reason: n.resolve_reason ?? null,
  });
  return [
    ...resolved.map((n) => item(n, "resolved", n.resolved_at)),
    ...dismissed.map((n) => item(n, "dismissed", dismissedAt.get(n.id) ?? null)),
  ].sort((a, b) => (b.closed_at ?? b.created_at).localeCompare(a.closed_at ?? a.created_at));
}

export interface StaffNotificationContext {
  talents: TalentLite[];
  invoices: Record<string, InvoiceFacts>;
}
/** Namen der Creator und Angaben zu den Rechnungen der Team-Meldungen (der tägliche Prüflauf speichert nur die Nummer). */
export async function loadStaffNotificationContext(api: Api, orgId: string, invoiceIds: readonly string[]): Promise<StaffNotificationContext> {
  const ids = [...new Set(invoiceIds)];
  const [talents, invoices] = await Promise.all([
    api.select<TalentLite>("talents", { columns: "id, first_name, last_name, display_name, status", filters: [["org_id", "eq", orgId]] }),
    ids.length
      ? api.select<{ id: string; number: string | null; brand_id: string | null; deal_id: string | null; gross_cents: number | null; currency: string; effective_due_date: string | null; document_due_date: string | null }>("invoices", {
          columns: "id, number, brand_id, deal_id, gross_cents, currency, effective_due_date, document_due_date",
          filters: [["org_id", "eq", orgId], ["id", "in", ids]],
        })
      : Promise.resolve([]),
  ]);
  const dealIds = [...new Set(invoices.filter((i) => !i.brand_id && i.deal_id).map((i) => i.deal_id!))];
  const deals = dealIds.length ? await api.select<{ id: string; brand_id: string }>("deals", { columns: "id, brand_id", filters: [["org_id", "eq", orgId], ["id", "in", dealIds]] }) : [];
  const brandOfDeal = new Map(deals.map((d) => [d.id, d.brand_id]));
  const brands = await brandsOf(api, orgId, invoices.map((i) => i.brand_id ?? (i.deal_id ? brandOfDeal.get(i.deal_id) ?? null : null)));
  return {
    talents,
    invoices: Object.fromEntries(invoices.map((i) => {
      const brandId = i.brand_id ?? (i.deal_id ? brandOfDeal.get(i.deal_id) : undefined);
      return [i.id, { number: i.number, brand: brandId ? brands[brandId] ?? null : null, gross_cents: i.gross_cents, currency: i.currency, due: i.effective_due_date ?? i.document_due_date }];
    })),
  };
}

/** Als gelesen markieren (auch Aufgaben: gelesen heißt nicht erledigt). Liefert die Zahl der markierten Meldungen. */
export async function markNotificationsRead(api: Api, ids: readonly string[]): Promise<number> {
  if (!ids.length) return 0;
  return api.rpc<number>("notification_mark_read", { p_ids: [...ids] });
}

/** Hinweis ausblenden; Aufgaben lehnt die Datenbank ab („Aufgaben verschwinden, sobald sie erledigt sind.“). */
export async function dismissNotification(api: Api, id: string): Promise<void> {
  await api.rpc("notification_dismiss", { p_id: id });
}

/**
 * Mehrere Hinweise auf einmal ausblenden (0041, notification_dismiss_many): nur die übergebenen, nur in dieser Agentur und
 * Zielgruppe; Aufgaben lässt die Datenbank stehen. Liefert die Zahl der ausgeblendeten Hinweise.
 */
export async function dismissNotifications(api: Api, scope: NotificationScope, ids: readonly string[]): Promise<number> {
  if (!ids.length) return 0;
  return Number(await api.rpc<number>("notification_dismiss_many", { p_org: scope.orgId, p_audience: scope.audience, p_ids: [...ids] })) || 0;
}

/** Ausgeblendete Hinweise wieder einblenden (Rückgängig). */
export async function restoreNotifications(api: Api, ids: readonly string[]): Promise<number> {
  if (!ids.length) return 0;
  return Number(await api.rpc<number>("notification_restore", { p_ids: [...ids] })) || 0;
}

/**
 * Das Team schließt eine Aufgabe mit Grund (z. B. „Rechnung kam per Post“); Creator können das nicht (0041).
 * Liefert true, damit der Dialog nach Erfolg schließt (Befund 21).
 */
export async function closeTask(api: Api, id: string, reason: string): Promise<true> {
  await api.rpc("notification_close_task", { p_id: id, p_reason: reason });
  return true;
}

/** Aufgabe übernehmen (selbst), einer Person zuweisen (Admin) oder abgeben (0056, Befund 16). */
export async function assignTask(api: Api, id: string, opts: { userId?: string | null; release?: boolean } = {}): Promise<true> {
  await api.rpc("notification_assign", { p_id: id, ...(opts.userId ? { p_user: opts.userId } : {}), ...(opts.release ? { p_release: true } : {}) });
  return true;
}

/**
 * Zahlungsnachfrage direkt in der Meldung beantworten (TA-166): „paid“ oder „still_overdue“. Die Datenbank erledigt
 * damit die Aufgabe und meldet dem Team „Zahlung gemeldet“ bzw. „Geld fehlt“ (0012).
 */
export async function answerPaymentCheck(api: Api, invoiceId: string, response: "paid" | "still_overdue"): Promise<{ status: string }> {
  return api.rpc<{ status: string }>("invoice_report_payment", { p_invoice: invoiceId, p_response: response });
}

// ── Kanäle ──────────────────────────────────────────────────────────────────

export interface Channels {
  /** Agentur: WhatsApp-Hinweise an Creator (org_settings.whatsapp_mode) */
  whatsappMode: "off" | "dry_run" | "live" | null;
  /** Creator: eigene Adresse für Mails und eigene Einwilligung in WhatsApp-Hinweise */
  email: string | null;
  whatsappPhone: string | null;
  whatsappOptIn: string | null;
  /** Desktop-Hinweis bei neuen Nachrichten (user_preferences, Standard an) */
  desktop: boolean;
  hasPrefsRow: boolean;
  /** Agentur: Aufgaben per E-Mail an mich (memberships.email_tasks, 0035) */
  emailTasks: boolean | null;
  /** Creator: reine Info-Mails abbestellt (talents.email_info_off, 0035) */
  emailInfoOff: boolean | null;
  /** Agentur: Sammelmail bei ungelesenen Chat-Nachrichten (memberships.email_chat, 0041) */
  emailChat: boolean | null;
  /** Creator: Chat-Mails abbestellt (talents.email_chat_off, 0041) */
  chatMailOff: boolean | null;
  /** Creator: Sprache der Mails (talents.language, null = Sprache des Portals, sonst der Agentur; app.talent_language) */
  language: "de" | "en" | null;
  /** Aufgaben und Hinweise sofort oder einmal am Tag gebündelt (0056): Creator talents.task_mail_mode, Team memberships.email_tasks_mode */
  taskMode: "instant" | "daily" | null;
  /** Creator: wer die Mail-Wünsche zuletzt geändert hat (creator, team, link, complaint) */
  prefsChangedVia: string | null;
  prefsChangedAt: string | null;
  /** Eigene Zeitzone der Person (user_preferences.time_zone, 0041) */
  timeZone: string | null;
  /** Team: persönliche Antwortadresse und Signatur, Abwesenheit mit Vertretung (0056) */
  replyTo: string | null;
  signature: string | null;
  awayFrom: string | null;
  awayUntil: string | null;
  awaySubstitute: string | null;
  awayNote: string | null;
}

export async function loadChannels(api: Api, input: { portal: Portal; orgId: string; talentId?: string | null; userId: string | null }): Promise<Channels> {
  const prefs = input.userId
    ? await api.select<{ desktop_notifications: boolean; time_zone: string | null }>("user_preferences", { columns: "desktop_notifications, time_zone", filters: [["user_id", "eq", input.userId]] }).catch(() => [])
    : [];
  const out: Channels = {
    whatsappMode: null, email: null, whatsappPhone: null, whatsappOptIn: null,
    desktop: prefs[0]?.desktop_notifications ?? true, hasPrefsRow: prefs.length > 0, emailTasks: null, emailInfoOff: null,
    emailChat: null, chatMailOff: null, language: null, taskMode: null, prefsChangedVia: null, prefsChangedAt: null,
    timeZone: prefs[0]?.time_zone ?? null, replyTo: null, signature: null, awayFrom: null, awayUntil: null, awaySubstitute: null, awayNote: null,
  };
  if (input.portal === "agentur") {
    type M = { email_tasks: boolean; email_chat: boolean; email_tasks_mode: "instant" | "daily"; mail_reply_to: string | null; mail_signature: string | null;
      away_from: string | null; away_until: string | null; away_substitute: string | null; away_note: string | null };
    const [s, m] = await Promise.all([
      api.select<{ whatsapp_mode: Channels["whatsappMode"] }>("org_settings", { columns: "whatsapp_mode", filters: [["org_id", "eq", input.orgId]] }).catch(() => []),
      input.userId ? api.select<M>("memberships", {
        columns: "email_tasks, email_chat, email_tasks_mode, mail_reply_to, mail_signature, away_from, away_until, away_substitute, away_note",
        filters: [["org_id", "eq", input.orgId], ["user_id", "eq", input.userId]],
      }).catch(() => [] as M[]) : Promise.resolve([] as M[]),
    ]);
    out.whatsappMode = s[0]?.whatsapp_mode ?? null;
    out.emailTasks = m[0]?.email_tasks ?? null;
    out.emailChat = m[0]?.email_chat ?? null;
    out.taskMode = m[0]?.email_tasks_mode ?? null;
    out.replyTo = m[0]?.mail_reply_to ?? null;
    out.signature = m[0]?.mail_signature ?? null;
    out.awayFrom = m[0]?.away_from ?? null;
    out.awayUntil = m[0]?.away_until ?? null;
    out.awaySubstitute = m[0]?.away_substitute ?? null;
    out.awayNote = m[0]?.away_note ?? null;
  } else if (input.talentId) {
    const t = await api.select<{ email: string | null; whatsapp_phone: string | null; whatsapp_opt_in_at: string | null; email_info_off: boolean; email_chat_off: boolean;
      language: "de" | "en" | null; task_mail_mode: "instant" | "daily"; mail_prefs_changed_via: string | null; mail_prefs_changed_at: string | null }>("talents", {
      columns: "email, whatsapp_phone, whatsapp_opt_in_at, email_info_off, email_chat_off, language, task_mail_mode, mail_prefs_changed_via, mail_prefs_changed_at",
      filters: [["org_id", "eq", input.orgId], ["id", "eq", input.talentId]],
    });
    out.chatMailOff = t[0]?.email_chat_off ?? null;
    out.language = t[0]?.language ?? null;
    out.email = t[0]?.email ?? null;
    out.whatsappPhone = t[0]?.whatsapp_phone ?? null;
    out.whatsappOptIn = t[0]?.whatsapp_opt_in_at ?? null;
    out.emailInfoOff = t[0]?.email_info_off ?? null;
    out.taskMode = t[0]?.task_mail_mode ?? null;
    out.prefsChangedVia = t[0]?.mail_prefs_changed_via ?? null;
    out.prefsChangedAt = t[0]?.mail_prefs_changed_at ?? null;
  }
  return out;
}

/** Eigene Zeitzone (user_preferences.time_zone); null = Zone des Browsers bzw. der Agentur (Befund 15). */
export async function saveTimeZone(api: Api, userId: string, zone: string | null): Promise<void> {
  const rows = await api.update<{ user_id: string }>("user_preferences", [["user_id", "eq", userId]], { time_zone: zone, updated_at: new Date().toISOString() }, { columns: "user_id" });
  if (!rows.length) await api.insert("user_preferences", { user_id: userId, time_zone: zone }, { columns: "user_id" });
}

/** Team: Aufgaben sofort oder als Tagesübersicht, persönliche Antwortadresse und Signatur (0056). */
export async function saveMemberMailPrefs(api: Api, orgId: string, p: { tasksMode?: "instant" | "daily"; replyTo?: string; signature?: string; clear?: boolean }): Promise<void> {
  await api.rpc("membership_set_mail_prefs", {
    p_org: orgId, ...(p.tasksMode ? { p_tasks_mode: p.tasksMode } : {}), ...(p.replyTo !== undefined ? { p_reply_to: p.replyTo } : {}),
    ...(p.signature !== undefined ? { p_signature: p.signature } : {}), ...(p.clear ? { p_clear: true } : {}),
  });
}

/** Team: Abwesenheit mit Vertretung (0056); ohne Datum = keine Abwesenheit. */
export async function saveAbsence(api: Api, orgId: string, a: { from: string | null; until: string | null; substitute: string | null; note: string | null }): Promise<void> {
  await api.rpc("membership_set_absence", { p_org: orgId, p_from: a.from, p_until: a.until, p_substitute: a.substitute, p_note: a.note });
}

/** Desktop-Hinweis ein- oder ausschalten (eigene Zeile in user_preferences, anlegen falls noch keine da ist). */
export async function saveDesktopPreference(api: Api, userId: string, value: boolean): Promise<void> {
  const rows = await api.update<{ user_id: string }>("user_preferences", [["user_id", "eq", userId]], { desktop_notifications: value, updated_at: new Date().toISOString() }, { columns: "user_id" });
  if (!rows.length) await api.insert("user_preferences", { user_id: userId, desktop_notifications: value }, { columns: "user_id" });
}

// ── Zähler für die Glocke ───────────────────────────────────────────────────

export interface UnreadScope {
  portal: Portal;
  orgId: string;
  /** Creator-Portal: eigener Talent-Datensatz */
  talentId?: string | null;
}
export interface UnreadData {
  count: UnreadCount;
  /** Fäden mit ungelesenen Nachrichten, neueste zuerst (für den Desktop-Hinweis) */
  latest: { talentId: string; lastAt: string | null; lastBody: string | null }[];
}

/** Ungelesene Nachrichten und Meldungen der angemeldeten Person in dieser Agentur. */
export async function loadUnread(api: Api, s: UnreadScope): Promise<UnreadData> {
  const side: Side = s.portal === "agentur" ? "agency" : "talent";
  const threadFilters: Filter[] = [["org_id", "eq", s.orgId], ["my_side", "eq", side]];
  if (s.portal === "creator" && s.talentId) threadFilters.push(["talent_id", "eq", s.talentId]);
  const [threads, notes] = await Promise.all([
    api.select<{ talent_id: string; unread: number; last_at: string | null; last_body: string | null; last_side: Side | null }>("my_chat_threads", {
      columns: "talent_id, unread, last_at, last_body, last_side", filters: threadFilters,
    }),
    api.select<{ id: string; unread: boolean; is_task: boolean }>("my_notifications", {
      columns: "id, unread, is_task",
      filters: scopeFilters({ orgId: s.orgId, audience: s.portal === "agentur" ? "staff" : "talent", talentId: s.portal === "creator" ? s.talentId ?? null : null }),
    }),
  ]);
  const latest = threads
    .filter((t) => Number(t.unread) > 0)
    .sort((a, b) => (b.last_at ?? "").localeCompare(a.last_at ?? ""))
    .map((t) => ({ talentId: t.talent_id, lastAt: t.last_at, lastBody: t.last_side !== side ? t.last_body : null }));
  return { count: unreadSummary(threads, notes), latest };
}

/** Aufgaben-Mails an die angemeldete Person ein- oder ausschalten (0035). */
export async function saveEmailTasks(api: Api, orgId: string, on: boolean): Promise<void> {
  await api.rpc("membership_set_email_tasks", { p_org: orgId, p_on: on });
}

/** Sammelmail bei ungelesenen Chat-Nachrichten an die angemeldete Person ein- oder ausschalten (0041). */
export async function saveEmailChat(api: Api, orgId: string, on: boolean): Promise<void> {
  await api.rpc("membership_set_email_chat", { p_org: orgId, p_on: on });
}

/** Creator: Chat-Mails, Sprache der Mails (auch zurück auf „wie die Agentur“) und Sammelmail (0041, 0056). */
export async function saveTalentPreferences(api: Api, talentId: string, prefs: { language?: "de" | "en" | null; chatMail?: boolean; taskMode?: "instant" | "daily" }): Promise<void> {
  await api.rpc("talent_set_preferences", {
    p_talent: talentId,
    ...(prefs.language ? { p_language: prefs.language } : prefs.language === null ? { p_clear_language: true } : {}),
    ...(prefs.chatMail !== undefined ? { p_chat_mail: prefs.chatMail } : {}),
    ...(prefs.taskMode ? { p_task_mode: prefs.taskMode } : {}),
  });
}

/** Agentur stellt Mails an einen Creator ab oder an (auf dessen Wunsch, mit Protokoll; 0056, Befund 12). */
export async function saveTalentMailPrefs(api: Api, talentId: string, p: { infoMail?: boolean; chatMail?: boolean; taskMode?: "instant" | "daily" }): Promise<void> {
  await api.rpc("talent_set_mail_prefs", {
    p_talent: talentId, ...(p.infoMail !== undefined ? { p_info_mail: p.infoMail } : {}), ...(p.chatMail !== undefined ? { p_chat_mail: p.chatMail } : {}),
    ...(p.taskMode ? { p_task_mode: p.taskMode } : {}),
  });
}

// ── Erreichbarkeit eines Creators (Chat-Kopf der Agentur; 0056, Befunde 3, 10, 12) ─────────────

export interface TalentReach {
  hasAccount: boolean;
  email: string | null;
  infoMail: boolean;
  chatMail: boolean;
  taskMode: "instant" | "daily";
  changedVia: string | null;
  changedAt: string | null;
  /** letzte Mail an diese Adresse (mail_address_status): zurückgekommen, gescheitert, als Spam gemeldet */
  lastProblem: { state: string; detail: string | null; at: string } | null;
}

export async function loadTalentReach(api: Api, orgId: string, talentId: string): Promise<TalentReach> {
  const [t, links, status] = await Promise.all([
    api.select<{ email: string | null; email_info_off: boolean; email_chat_off: boolean; task_mail_mode: "instant" | "daily"; mail_prefs_changed_via: string | null; mail_prefs_changed_at: string | null }>("talents", {
      columns: "email, email_info_off, email_chat_off, task_mail_mode, mail_prefs_changed_via, mail_prefs_changed_at", filters: [["org_id", "eq", orgId], ["id", "eq", talentId]],
    }),
    api.select<{ talent_id: string }>("talent_user_links", { columns: "talent_id", filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId]] }).catch(() => []),
    // Nur Mails an den Creator selbst (SICHT-AGENTUR-REST-22): ein Rückläufer an die Rechnungsadresse der Marke hängt über die
    // Rechnung zwar am Creator, gehört aber auf die Markenseite
    api.select<{ state: string; detail: string | null; at: string }>("mail_address_status", {
      columns: "state, detail, at", filters: [["org_id", "eq", orgId], ["talent_id", "eq", talentId], ["recipient_kind", "eq", "talent"]], order: [["at", "desc"]], limit: 1,
    }).catch(() => []),
  ]);
  const row = t[0];
  const last = status[0];
  return {
    hasAccount: links.length > 0,
    email: row?.email ?? null,
    infoMail: !(row?.email_info_off ?? false),
    chatMail: !(row?.email_chat_off ?? false),
    taskMode: row?.task_mail_mode ?? "instant",
    changedVia: row?.mail_prefs_changed_via ?? null,
    changedAt: row?.mail_prefs_changed_at ?? null,
    lastProblem: last && ["bounced", "failed", "complained"].includes(last.state) ? last : null,
  };
}

// ── Ansprechpartner und Abwesenheit (Chat-Kopf beider Seiten) ───────────────

export interface ChatContact {
  user_id: string;
  display_name: string;
  assigned: boolean;
  away_until: string | null;
  substitute_name: string | null;
}
export async function loadChatContacts(api: Api, talentId: string): Promise<ChatContact[]> {
  return (await api.read<ChatContact[] | null>("chat_contacts", { p_talent: talentId }).catch(() => null)) ?? [];
}

// ── Rundnachricht (0056) ────────────────────────────────────────────────────

export type BroadcastAudience = "all" | "group" | "selection";
export interface BroadcastPreview {
  total: number;
  portal: number;
  mail_only: number;
  unreachable: string[];
  recipients?: { talent_id: string; name: string; via: "portal" | "mail" | "none" }[];
}
export interface BroadcastResult extends BroadcastPreview {
  broadcast_id: string;
  sent: number;
}
/** Vorschau: wer bekommt die Rundnachricht wie (nur lesend). Rechte prüft die Datenbank wie beim Kalender. */
export async function broadcastPreview(api: Api, orgId: string, a: { audience: BroadcastAudience; groupId?: string | null; talentIds?: string[] }): Promise<BroadcastPreview> {
  return api.read<BroadcastPreview>("chat_broadcast_preview", {
    p_org: orgId, p_audience: a.audience, ...(a.groupId ? { p_group: a.groupId } : {}), ...(a.talentIds ? { p_talents: a.talentIds } : {}),
  });
}
/** Rundnachricht senden: eine eigene Nachricht in jedem Einzelfaden; Antworten bleiben privat. */
export async function broadcast(api: Api, orgId: string, a: { audience: BroadcastAudience; groupId?: string | null; talentIds?: string[]; body: string }): Promise<BroadcastResult> {
  return api.rpc<BroadcastResult>("chat_broadcast", {
    p_org: orgId, p_audience: a.audience, ...(a.groupId ? { p_group: a.groupId } : {}), ...(a.talentIds ? { p_talents: a.talentIds } : {}), p_body: a.body,
  });
}

// ── Erinnerungen der Agentur (0041, nur Admins ändern) ────────────────

export interface ReminderSettings {
  content_deadline_days: number;
  usage_rights_days: number;
  invoice_missing_days: number;
  chat_digest_minutes: number;
  /** Uhrzeit der täglichen Übersicht (0094), 5 bis 11 Uhr in der Zone der Person, frühestens nach den Tagesläufen */
  digest_hour: number;
}
export const DIGEST_HOURS = [5, 6, 7, 8, 9, 10, 11] as const;
export const DEFAULT_REMINDERS: ReminderSettings = { content_deadline_days: 2, usage_rights_days: 30, invoice_missing_days: 7, chat_digest_minutes: 15, digest_hour: 7 };

export async function loadReminders(api: Api, orgId: string): Promise<ReminderSettings> {
  const rows = await api.select<ReminderSettings>("org_reminder_settings", {
    columns: "content_deadline_days, usage_rights_days, invoice_missing_days, chat_digest_minutes, digest_hour", filters: [["org_id", "eq", orgId]],
  }).catch(() => []);
  return rows[0] ? { ...DEFAULT_REMINDERS, ...rows[0] } : DEFAULT_REMINDERS;
}
export async function saveReminders(api: Api, orgId: string, r: ReminderSettings): Promise<void> {
  await api.rpc("org_reminders_save", {
    p_org: orgId, p_content_deadline_days: r.content_deadline_days, p_usage_rights_days: r.usage_rights_days,
    p_invoice_missing_days: r.invoice_missing_days, p_chat_digest_minutes: r.chat_digest_minutes, p_digest_hour: r.digest_hour,
  });
}

/** Zähler je Agentur für den Agenturwechsler im Creator-Portal (0041, my_unread_by_org). */
export async function loadUnreadByOrg(api: Api): Promise<Record<string, number>> {
  const rows = await api.read<{ org_id: string; messages: number; tasks: number; notifications: number }[] | null>("my_unread_by_org", {}).catch(() => null);
  return Object.fromEntries((rows ?? []).map((r) => [r.org_id, Number(r.messages) + Number(r.tasks) + Number(r.notifications)]));
}

/**
 * Creator: reine Info-Mails abbestellen oder wieder erhalten (0035); Aufgaben-Mails kommen immer. Über talent_set_mail_prefs
 * (0056), damit festgehalten wird, wer wann geändert hat. orgId bleibt für die Aufrufer; die Datenbank kennt den Creator.
 */
export async function saveEmailInfoOff(api: Api, _orgId: string, talentId: string, off: boolean): Promise<void> {
  await api.rpc("talent_set_mail_prefs", { p_talent: talentId, p_info_mail: !off });
}
